import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AXIOS from "../../../../helpers/api";
import { BASE_API } from "../../../../helpers/apiUrl";
import { toast } from "../../../../helpers/toast";
import {
  CommonOthers,
  ErrorMessage,
  GetAllUsersData,
  GetSingleDataParams,
  Params,
  PostRequest,
  RegionalParams,
} from "../../../../types/redux";
import {
  BulkCreatePOData,
  CreateBatchPaymentData,
  CreatePOData,
  ForwardRequestToStepAboveData,
  GenerateInvoiceParams,
  GetAllBatchmentPaymentData,
  GetAllBatchmentPaymentParams,
  GetALLPOParams,
  GetAllSupplierData,
  GetAllTowersByBatchPaymentIdParams,
  GetCompletedTowerBySupplierParams,
  GetGeneratePdfDataParams,
  GetSingleBatchmentPaymentDataParams,
  GetSupperData,
  IrExcelUploadData,
  MarkPaidPaymentData,
  Pagination,
  PurchaseOrder,
  RejectBatchPaymentData,
  SupplierData,
  SupplierGetAllParams,
  SupplierUserCountParams,
  TGetCompletedData,
  UpdateBatchPaymentData,
  UpdatePOData,
} from "../../../../types/redux/NP/supplier";

// create a supplier
export const createSupplierAsync = createAsyncThunk<
  null,
  PostRequest<SupplierData, CommonOthers>
>(
  "np-supplier/create-supplier",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(BASE_API.createSupplier, data);
      reRender(true);
      toast("success", "Supplier create successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Supplier create fail");
      return rejectWithValue(error);
    }
  }
);

// Update a supplier
export const updateSupplierAsync = createAsyncThunk<
  null,
  PostRequest<SupplierData, CommonOthers>
>(
  "np-supplier/update-supplier",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(`${BASE_API.updateSupplier}/${id}`, data);
      reRender(true);
      toast("success", "Supplier update successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Supplier create fail");
      return rejectWithValue(error);
    }
  }
);

// get all supplier
export const getAllSupplierAsync = createAsyncThunk<
  GetAllSupplierData[],
  Params<SupplierGetAllParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getAllSupplier", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getAllSupplier, {
      params,
    });

    return data.data?.getData as GetAllSupplierData[];
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get all supplier data fail");
    return rejectWithValue(error);
  }
});

// get all supplier
export const getSupplierByIdAsync = createAsyncThunk<
  GetSupperData[],
  Params<{ id: string }>,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getAllSupplierById", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.getAllSupplier}/${params?.id}`);

    return data.data?.data as GetSupperData[];
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get supplier data fail");
    return rejectWithValue(error);
  }
});

// get count all supplier
export const getCountSupplierAsync = createAsyncThunk<
  number,
  Params<RegionalParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getCountSupplier", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getCountSupplier, { params });
    return data.data?.getData || 0;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get supplier count data fail");
    return rejectWithValue(error);
  }
});

// get count all supplier
export const getSupplierProfileAsync = createAsyncThunk<
  any,
  undefined,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getSupplierProfile", async (_, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getSupplierProfile);
    return data.data?.getData || 0;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get supplier profile data fail");
    return rejectWithValue(error);
  }
});

// get supplier user
export const getSupplierUserAsync = createAsyncThunk<
  GetAllUsersData[],
  Params<SupplierUserCountParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getSingleRegion", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.getSupplierUser}/${params?.id}`, {
      params,
    });

    return data.data?.getData as GetAllUsersData[];
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get user by supplier fail");
    return rejectWithValue(error);
  }
});

// get count all user supplier
export const getCountUserSupplierAsync = createAsyncThunk<
  number,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>(
  "np-supplier/getCountUserSupplier",
  async ({ params }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(
        `${BASE_API.getCountUserSupplier}/${params?.id}`
      );
      return data.data?.getData || 0;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Get supplier user count data fail");
      return rejectWithValue(error);
    }
  }
);

// get single supplier
export const getSingleSupplierAsync = createAsyncThunk<
  any,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-network/getSingleSupplier", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.getSingleSupplier}/${params?.id}`);

    return data.data?.data as any;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get supplier fail");
    return rejectWithValue(error);
  }
});

// create a PO
export const createPOAsync = createAsyncThunk<
  null,
  PostRequest<CreatePOData, CommonOthers>
>(
  "np-supplier/create-po",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(BASE_API.createPO, data);
      reRender(true);
      toast("success", "PO create successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "PO create fail");
      return rejectWithValue(error);
    }
  }
);

// get all supplier
export const getAllPOAsync = createAsyncThunk<
  {
    data: PurchaseOrder[];
    pagination: Pagination;
  },
  Params<GetALLPOParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getAllPO", async ({ params }, { rejectWithValue }) => {
  try {
    const response = await AXIOS.get(BASE_API.getAllPO, { params });

    return {
      data: response?.data?.data,
      pagination: response?.data?.pagination,
    };
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get all PO data failed");
    return rejectWithValue(error);
  }
});

// get single PO
export const getSinglePOAsync = createAsyncThunk<
  any,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-network/getSinglePO", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.getSinglePO}/${params?.id}`);

    return data?.data as any;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get Single PO fail");
    return rejectWithValue(error);
  }
});

// Update batchment payment
export const updatePOAsync = createAsyncThunk<
  null,
  PostRequest<UpdatePOData, CommonOthers>
>(
  "np-supplier/updatePO",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.patch(`${BASE_API.updatePO}/${id}`, data);
      reRender(true);
      toast("success", "PO update successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "PO update fail");
      return rejectWithValue(error);
    }
  }
);

// create a batchment payment
type BatchCreateOthers = {
  callback: (val: string) => void;
};

// type PostRequests<T, O = undefined> = {
//   data: T;
//   params?: Record<string, any>;
//   others: O;
// };

export const createBatchPaymentAsync = createAsyncThunk<
  null,
  PostRequest<CreateBatchPaymentData, BatchCreateOthers>
>(
  "np-supplier/create-batchPayment",
  async ({ data, others: { callback } }, { rejectWithValue }) => {
    try {
      const res = await AXIOS.post(BASE_API.createBatchPayment, data);

      toast("success", "Batch payment create successfully");
      callback(res?.data?.data?._id);
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Batch payment create fail");
      return rejectWithValue(error);
    }
  }
);

// get all batchment payment
export const getAllBatchmentPaymentAsync = createAsyncThunk<
  GetAllBatchmentPaymentData,
  Params<GetAllBatchmentPaymentParams>,
  {
    rejectValue: ErrorMessage;
  }
>(
  "np-supplier/getAllBatchmentPayment",
  async ({ params }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(BASE_API.getAllBatchmentPayment, {
        params,
      });

      return data?.data;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Get all batchment payment data fail");
      return rejectWithValue(error);
    }
  }
);

// get single batchment payment
export const getSingleBatchmentPaymentAsync = createAsyncThunk<
  any,
  Params<GetSingleBatchmentPaymentDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>(
  "np-network/getSingleBatchmentPayment",
  async ({ params }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(
        `${BASE_API.getSingleBatchmentPayment}/${params?.id}`
      );

      return data?.data?.data as any;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Get Single Batchment Payment fail");
      return rejectWithValue(error);
    }
  }
);

// Update batchment payment
export const updateBatchPaymentAsync = createAsyncThunk<
  null,
  PostRequest<UpdateBatchPaymentData, CommonOthers>
>(
  "np-supplier/update-batchPayment",
  async (
    { data, others: { reRender, id, role, message } },
    { rejectWithValue }
  ) => {
    try {
      await AXIOS.patch(`${BASE_API.updateBatchPayment}/${id}`, data);
      let successMsg = message || "VAT added successfully";
      switch (role) {
        case "gr":
          successMsg = "GR number add successfully";
          break;
      }
      reRender(true);
      toast("success", successMsg);
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Batch payment update fail");
      return rejectWithValue(error);
    }
  }
);

// Forward Request To Step Above
export const forwardRequestToStepAboveAsync = createAsyncThunk<
  null,
  PostRequest<ForwardRequestToStepAboveData, CommonOthers>
>(
  "np-supplier/forwardRequestToStepAbove",
  async ({ data, others: { reRender, id, role } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(`${BASE_API.forwardRequestToStepAbove}/${id}`, data);
      let successMsg = "Request forwarded successfully";
      switch (role) {
        case "vendor":
          successMsg = "Request sent to Zone";
          break;
        case "region":
          successMsg = "Request sent to Cluster Ops";
          break;
        case "cluster":
          successMsg = "Request sent to Energy Ops";
          break;
        case "energyOps":
          successMsg = "Request sent to Director of Ops";
          break;
        case "directorOps":
          successMsg = "Request sent to GR";
          break;
        case "gr":
          successMsg = "Request sent to Finance";
          break;
        case "finance":
          successMsg = "Payment request submitted";
          break;
      }
      reRender(true);
      toast("success", successMsg);
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Request forward fail");
      return rejectWithValue(error);
    }
  }
);

// payment mark as paid
export const markPaidPaymentAsync = createAsyncThunk<
  null,
  PostRequest<MarkPaidPaymentData, CommonOthers>
>(
  "np-supplier/markPaidPayment",
  async ({ data, others: { reRender, id, role } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(`${BASE_API.markPaidPayment}/${id}`, data);
      let successMsg = "Payment Approved.";

      reRender(true);
      toast("success", successMsg);
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Payment paid fail");
      return rejectWithValue(error);
    }
  }
);

// Reject Batch Payment
export const rejectBatchPaymentAsync = createAsyncThunk<
  null,
  PostRequest<RejectBatchPaymentData, CommonOthers>
>(
  "np-supplier/rejectBatchPayment",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(`${BASE_API.rejectBatchPayment}/${id}`, data);
      reRender(true);
      toast("success", "Batch payent reject successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Batch payment reject fail");
      return rejectWithValue(error);
    }
  }
);

// Get All Completed Tower By Supplier
export const getCompletedTowerBySupplierAsync = createAsyncThunk<
  TGetCompletedData,
  Params<GetCompletedTowerBySupplierParams>,
  {
    rejectValue: ErrorMessage;
  }
>(
  "np-supplier/getCompletedTowerBySupplier",
  async ({ params }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(BASE_API.getCompletedTowerBySupplier, {
        params,
      });

      return data?.data as TGetCompletedData;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast(
        "error",
        error?.message || "Get all completed tower by supplier data fail"
      );
      return rejectWithValue(error);
    }
  }
);

// Get All Towers By Batch Payment Id
export const getAllTowersByBatchPaymentIdAsync = createAsyncThunk<
  any,
  Params<GetAllTowersByBatchPaymentIdParams>,
  {
    rejectValue: ErrorMessage;
  }
>(
  "np-network/getAllTowersByBatchPaymentId",
  async ({ params }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(
        `${BASE_API.getAllTowersByBatchPaymentId}/${params?.id}`
      );

      return data?.data?.data as any;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast(
        "error",
        error?.message || "Get All Tower Batch Payment By ID fail"
      );
      return rejectWithValue(error);
    }
  }
);

// Generate Invoice
export const generateInvoiceAsync = createAsyncThunk<
  any,
  Params<GenerateInvoiceParams>,
  {
    rejectValue: ErrorMessage;
  }
>("np-network/generateInvoice", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.generateInvoice}/${params?.id}`);

    return data?.data?.data as any;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Generate invoice fail");
    return rejectWithValue(error);
  }
});

// Get Single PO For Vendor
export const getSinglePOForVendorAsync = createAsyncThunk<
  any,
  undefined,
  {
    rejectValue: ErrorMessage;
  }
>("np-supplier/getSinglePOForVendor", async (_, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.getSinglePOForVendor);
    return data?.data?.data as any;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get Single PO For Vendor fail");
    return rejectWithValue(error);
  }
});

// create a bulk PO
export const bulkCreatePOAsync = createAsyncThunk<
  null,
  PostRequest<BulkCreatePOData, CommonOthers>
>(
  "np-supplier/bulk-create-po",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      const response = await AXIOS.post(BASE_API.bulkCreatePO, data);

      // If response is successful, show success rows (if returned)
      const resultData = response?.data;
      const { results } = resultData || {};

      if (results?.created?.length > 0) {
        results.created.forEach((item: any) => {
          toast("success", `PO imported successfully`);
        });
      }

      reRender(true);
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as any;

      const { results } = error || {};

      // ✅ Show duplicate errors
      if (results?.duplicates?.length > 0) {
        results.duplicates.forEach((item: any) => {
          toast("error", `Row ${item.index + 1}: ${item.error}`);
        });
      }

      // ✅ Show validation errors
      if (results?.validationErrors?.length > 0) {
        results.validationErrors.forEach((item: any) => {
          toast("error", `Row ${item.index + 1}: ${item.error}`);
        });
      }

      // ✅ Show generic row errors
      if (results?.errors?.length > 0) {
        results.errors.forEach((item: any) => {
          toast("error", `Row ${item.index + 1}: ${item.error}`);
        });
      }

      // ❌ Fallback error if nothing specific
      if (
        !results?.duplicates?.length &&
        !results?.validationErrors?.length &&
        !results?.errors?.length
      ) {
        toast("error", error?.message || "PO import failed");
      }

      return rejectWithValue(error);
    }
  }
);

// Forward Request To Step Above
export const addBatchTaskAsync = createAsyncThunk<
  null,
  PostRequest<object, CommonOthers>
>(
  "np-supplier/forwardRequestToStepAbove",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      const res = await AXIOS.post(`${BASE_API.addBatchTask}/${id}`, data);

      reRender(true);
      toast("success", res.data?.data?.message || "Task added successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Task add fail");
      return rejectWithValue(error);
    }
  }
);

type ParamsPDF = {
  others: {
    reRender: (val: boolean) => void;
  };
} & Params<GetGeneratePdfDataParams>;

// get generate pdf by batch id
export const getGeneratePdfAsync = createAsyncThunk<
  any,
  ParamsPDF,
  {
    rejectValue: ErrorMessage;
  }
>(
  "np-network/getGeneratePdf",
  async ({ params, others: { reRender } }, { rejectWithValue }) => {
    try {
      const data = await AXIOS.get(`${BASE_API.getGeneratePdf}/${params?.id}`);
      reRender(true);
      return data?.data?.data as any;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Get generate pdf fail");
      return rejectWithValue(error);
    }
  }
);

// irExcelUpload

export const irExcelUploadAsync = createAsyncThunk<
  null,
  PostRequest<IrExcelUploadData, CommonOthers>
>(
  "np-supplier/irExcelUpload",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      const response = await AXIOS.post(`${BASE_API.irExcelUpload}`, data);
      const resData = response.data;

      const failedRecords = resData?.data?.failedRecords;
      const hasFailed = failedRecords && failedRecords.length > 0;

      if (hasFailed) {
        const errorMsg = failedRecords[0]?.error || "Excel upload failed";
        toast("error", errorMsg);
        return rejectWithValue({ message: errorMsg });
      }

      // All good — show success
      reRender(true);
      toast("success", "Excel upload successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as any;

      const detailedError =
        error?.data?.failedRecords?.[0]?.error ||
        error?.message ||
        "Excel upload failed";

      toast("error", detailedError);
      return rejectWithValue(error);
    }
  }
);
